import loadable from '@loadable/component';
import { styled } from '@ori-ui/mui';
import { NavigationLinkType } from '@ori/graphql-schema';
import { MenuContentContainer as PbsMenuContent } from '@ori/personal-beauty-store';
import useTestId from '@ori/testid-generator';
import type { RefObject, VFC } from 'react';
import { Suspense, memo } from 'react';

import { Catalogues } from './Catalogues/Catalogues';
import { SkeletonSubCategoryDesktop, SkeletonSubCategoryMobile } from '~/components/Skeleton';
import { DESKTOP_BREAKPOINT } from '~/constants';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { useMobileWidth } from '~/hooks';
import type { CategoryContentProps } from '~/models';

const BurgerMenuContentRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    display: 'flex',
    width: '80%',
  },
}));

const StyledPbsMenuContent = styled(PbsMenuContent)(({ theme }) => ({
  marginLeft: '4rem',
  marginTop: '3rem',
}));

const LazyInspiration = loadable(async () =>
  import('./Inspiration/Inspiration').then((mod) => mod.Inspiration),
) as VFC;
const LazyCategoryContent = loadable(async () =>
  import('./CategoryContent/CategoryContent').then((mod) => mod.CategoryContent),
) as VFC;

const CategoryContent: VFC<CategoryContentProps> = memo(({ isMobile }) => (
  <Suspense fallback={isMobile ? <SkeletonSubCategoryMobile /> : <SkeletonSubCategoryDesktop />}>
    <LazyCategoryContent />
  </Suspense>
));

export interface BurgerMenuContentProps {
  contentRootRef?: RefObject<HTMLDivElement>;
}

export const BurgerMenuContent = ({ contentRootRef }: BurgerMenuContentProps) => {
  const isMobile = useMobileWidth();
  const { activeLink, pbsContentIsShown } = useNavigationContext();
  const { linkType, id } = activeLink ?? {};
  const { getTestId } = useTestId();

  const navigation = {
    isCatalogue: linkType === NavigationLinkType.Catalogue,
    isCategory: linkType === NavigationLinkType.Category,
    isInspiration: linkType === NavigationLinkType.Inspiration,
  };

  return (
    <BurgerMenuContentRoot
      ref={contentRootRef}
      data-testid={getTestId('burger-menu-content-root')}
    >
      {pbsContentIsShown ? (
        <StyledPbsMenuContent />
      ) : (
        <>
          {navigation.isCatalogue ? <Catalogues /> : null}
          {navigation.isCategory && id ? (
            <CategoryContent
              isMobile={isMobile}
              data-testid={getTestId('category-content')}
            />
          ) : null}
          {navigation.isInspiration ? <LazyInspiration /> : null}
        </>
      )}
    </BurgerMenuContentRoot>
  );
};
